<template>
  <!-- 表示切り替えボタン -->
  <div class="d-flex justify-end mb-2">
    <v-btn-toggle v-model="viewMode" density="comfortable" mandatory>
      <v-btn value="calendar" prepend-icon="$mdiCalendarMonth">
        カレンダー表示
      </v-btn>
      <v-btn value="list" prepend-icon="$mdiFormatListBulleted">
        リスト表示
      </v-btn>
    </v-btn-toggle>
  </div>

  <div v-if="viewMode === 'calendar'">
    <v-row>
      <!-- カレンダー表示 -->
      <v-col cols="7">
        <div ref="calendarContainer">
          <full-calendar :options="calendarOptions" />
        </div>
      </v-col>
      <v-col cols="5">
        <!-- 詳細パネル -->
        <v-card
          class="detail-panel"
          :style="{ height: calendarHeight + 'px' }"
          elevation="2"
        >
          <v-expand-transition>
            <div v-if="selectedMaintenanceItem">
              <v-card-title class="d-flex">
                <span class="title-text">{{
                  selectedMaintenanceItem.noticeInfoTitle
                }}</span>
                <div class="d-flex">
                  <v-chip
                    :color="selectedChipColor"
                    variant="flat"
                    size="small"
                  >
                    {{ selectedChipStatus }}
                  </v-chip>
                </div>
              </v-card-title>

              <v-card-text>
                <v-list-item class="px-0 mb-4">
                  <v-list-item-title
                    class="text-caption text-medium-emphasis mb-1"
                  >
                    メンテナンス予定期間
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-row dense>
                      <v-col cols="12">
                        <v-icon
                          icon="$mdiClockStart"
                          size="small"
                          class="me-2"
                        ></v-icon>
                        開始:
                        {{
                          formatMaintenanceDateTime(
                            selectedMaintenanceItem.maintenanceStartDateTime
                          )
                        }}
                      </v-col>
                      <v-col cols="12">
                        <v-icon
                          icon="$mdiClockEnd"
                          size="small"
                          class="me-2"
                        ></v-icon>
                        終了:
                        {{
                          formatMaintenanceDateTime(
                            selectedMaintenanceItem.maintenanceEndDateTime
                          )
                        }}
                      </v-col>
                    </v-row>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-divider class="mb-4"></v-divider>
                <div class="maintenance-description">
                  {{ selectedMaintenanceItem.noticeText }}
                </div>
              </v-card-text>
            </div>
          </v-expand-transition>

          <!-- 詳細パネル未選択時 -->
          <v-card-text
            v-if="!selectedMaintenanceItem && maintenanceItems.length > 0"
            class="align-center pa-8 detail-panel"
          >
            <v-icon
              icon="$mdiGestureTap"
              size="x-large"
              color="grey-lighten-1"
              class="mb-4"
            ></v-icon>
            <v-card-subtitle class="text-center message-text">
              日付のメンテナンス項目をクリックすると詳細が表示されます
            </v-card-subtitle>
          </v-card-text>

          <!-- メンテナンス情報なし -->
          <v-card-text
            v-if="maintenanceItems.length === 0"
            class="align-center pa-8 detail-panel"
          >
            <v-icon
              icon="$mdiCalendarAlert"
              size="x-large"
              color="grey-lighten-1"
              class="mb-4"
            ></v-icon>
            <v-card-subtitle class="text-center message-text">
              現在メンテナンス情報はありません
            </v-card-subtitle>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>

  <!-- リスト表示 -->
  <div v-if="viewMode === 'list'">
    <app-table
      primary-key="noticeInfoSeq"
      :fields="maintenanceFields"
      :items="maintenanceItem"
      :selectable="true"
      v-model="selectedItems"
      :hover="true"
      @click:row="handleRowClick"
      :tr-class="getRowClass"
      :per-page="-1"
    >
      <template #cell(_selected)="data">
        <v-checkbox-btn
          v-if="selectable && !data.item.isRead"
          :value="data.item"
          v-model="selectedItems"
          @click.stop
        />
      </template>
      <template #cell(maintenanceStartDateTime)="{ value }">
        {{ $filter.emptyTo($filter.datetime(value), "-") }}
      </template>
      <template #cell(maintenanceEndDateTime)="{ value }">
        {{ $filter.emptyTo($filter.datetime(value), "-") }}
      </template>
    </app-table>
    <div v-if="maintenanceItem.length === 0" class="text-center mt-5">
      <span v-if="isUnread">現在未読のメンテナンス情報はありません</span>
      <span v-else>現在メンテナンス情報はありません</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { AppTableHeader } from "@/components/AppTable.vue";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import allLocales from "@fullcalendar/core/locales-all";
import interactionPlugin from "@fullcalendar/interaction";
import type { DayCellContentArg, EventClickArg } from "@fullcalendar/core";
import type { NoticeInfo, NoticeInfoDetail } from "@/apis/InformationApi";
import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";

// カレンダーイベントの型定義
interface CalendarEvent {
  title: string;
  start: string;
  end: string;
  color: string;
  noticeInfoSeq: string;
  isRead: boolean;
}

export default defineComponent({
  name: "AppCalendar",
  components: {
    FullCalendar,
  },
  props: {
    maintenanceItems: {
      type: Array as PropType<NoticeInfo[]>,
      required: true,
    },
    isUnread: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // カレンダーとリストの表示切替
      viewMode: "list" as "calendar" | "list",
      selectedItems: [] as NoticeInfo[],
      selectedMaintenanceItem: null as NoticeInfoDetail | null,
      selectedChipStatus: "",
      selectedChipColor: "",
      calendarHeight: 0,
    };
  },
  watch: {
    // 親コンポーネントで制御できるようにviewModeとselectedItemsをemitで通知
    viewMode(newVal) {
      this.$emit("view-mode-changed", newVal);
    },
    selectedItems(newVal) {
      this.$emit("update:selected", newVal);
    },
  },
  computed: {
    // FullCalendarのオプション設定
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        allDayText: "終日",
        headerToolbar: {
          start: "today prev,next",
          center: "title",
          end: "today prev,next", // タイトル中央配置のため右側ボタンを左側と同じにしてCSSで非表示
        },
        // cssで土日の色分けをしているため、businessHoursはfalseに設定
        businessHours: false,
        // 日付セルの内容を日付の数字のみする（「日」を除去）
        dayCellContent: (arg: DayCellContentArg) =>
          arg.dayNumberText.replace("日", ""),
        height: "auto",
        locales: allLocales,
        locale: "ja",
        events: this.calendarEvents,
        datesSet: () => {
          // ビュー変更時に高さを更新（DOM更新後に実行）
          setTimeout(() => {
            this.updateCalendarHeight();
          }, 0);
        },
        eventClick: async (info: EventClickArg) => {
          // 未読の場合、既読にする処理を実行（代行権限の場合はスキップ）
          if (
            info.event.extendedProps.isRead === false &&
            !this.$service.permission.isDelegateUser()
          ) {
            await this.$api.information.putRead(
              info.event.extendedProps.noticeInfoSeq
            );
            // maintenanceItems内の同一のnoticeInfoSeqのisReadをtrueに更新
            const item = this.maintenanceItems.find(
              (item) =>
                item.noticeInfoSeq === info.event.extendedProps.noticeInfoSeq
            );
            if (item) {
              item.isRead = true;
            }
          }
          // お知らせ詳細を取得して表示
          this.selectedMaintenanceItem =
            await this.$api.information.getNotificationDetail(
              info.event.extendedProps.noticeInfoSeq
            );
          this.selectItemChip(this.selectedMaintenanceItem);
        },
      };
    },
    // maintenanceItemsからカレンダーイベントを生成
    calendarEvents(): CalendarEvent[] {
      // 今日の日付を取得（yyyy-mm-dd形式）
      const today = new Date().toLocaleDateString("sv-SE");
      return this.maintenanceItems
        .filter((item) => {
          // メンテナンス開始・終了日時が有効な場合のみカレンダーに表示
          return (
            this.isDateTimeValid(item.maintenanceStartDateTime) &&
            this.isDateTimeValid(item.maintenanceEndDateTime)
          );
        })
        .map((item) => {
          const startStr = item.maintenanceStartDateTime!.split("T")[0];
          const endStr = item.maintenanceEndDateTime!.split("T")[0];

          let end = endStr;
          if (startStr !== endStr) {
            // startとendが同じでない場合、endにプラス1日(FullCalendarの仕様)
            const endDate = new Date(endStr);
            endDate.setDate(endDate.getDate() + 1);
            end = endDate.toISOString().split("T")[0];
          }

          // 今日の日付の範囲内または、今日以降であれば「オレンジ:予定」、それ以前であれば「グレー:完了」とする
          let color = "";
          if ((today >= startStr && today <= endStr) || today < startStr) {
            color = "orange";
          } else {
            color = "grey";
          }

          return {
            title: item.noticeInfoTitle,
            start: startStr,
            end: end,
            color: color,
            noticeInfoSeq: item.noticeInfoSeq,
            isRead: item.isRead!,
          };
        });
    },
    // リストのフィールド定義
    maintenanceFields(): AppTableHeader<NoticeInfo>[] {
      return [
        {
          key: "maintenanceStartDateTime",
          title: "開始予定日時",
          width: "180px",
        },
        {
          key: "maintenanceEndDateTime",
          title: "終了予定日時",
          width: "180px",
        },
        { key: "noticeInfoTitle", title: "件名", width: "540px" },
      ];
    },
    /**
     * メンテナンスのitemを返却
     * 未読のみの表示の場合は、未読のみのitemを返却する。
     */
    maintenanceItem(): NoticeInfo[] {
      if (this.isUnread) {
        return this.maintenanceItems.filter((e) => e.isRead === false);
      }
      return this.maintenanceItems;
    },
  },
  mounted() {
    this.updateCalendarHeight();
  },
  methods: {
    updateCalendarHeight() {
      const calendarEl = this.$refs.calendarContainer as HTMLElement;
      if (calendarEl) {
        this.calendarHeight = calendarEl.clientHeight;
      }
    },
    // 詳細パネル内のチップのステータスと色を設定
    selectItemChip(selectedMaintenanceItem: NoticeInfoDetail) {
      //今日の日付（日本時間yyyy-mm-ddで取得）
      const today = new Date().toLocaleDateString("sv-SE");
      const start = selectedMaintenanceItem.maintenanceStartDateTime
        ? selectedMaintenanceItem.maintenanceStartDateTime.split("T")[0]
        : "";
      const end = selectedMaintenanceItem.maintenanceEndDateTime
        ? selectedMaintenanceItem.maintenanceEndDateTime.split("T")[0]
        : "";

      // 今日の日付の範囲内または、今日以降であれば「オレンジ:予定」、それ以前であれば「グレー:完了」とする
      if ((today >= start && today <= end) || today < start) {
        this.selectedChipStatus = "予定";
        this.selectedChipColor = "orange";
      } else {
        this.selectedChipStatus = "完了";
        this.selectedChipColor = "grey";
      }
    },
    // メンテナンス日時の表示形式
    formatMaintenanceDateTime(dateTimeStr: string | null): string {
      try {
        if (!dateTimeStr) return "-";
        const dateTime = parseISO(dateTimeStr);
        const dayOfWeek = format(dateTime, "EEE", { locale: ja });
        return format(dateTime, `yyyy年MM月dd日（${dayOfWeek}） HH:mm`, {
          locale: ja,
        });
      } catch {
        return dateTimeStr || "-";
      }
    },
    // お知らせ詳細表示
    async handleRowClick(item: NoticeInfo) {
      this.$emit("show-notification", item);
    },

    // お知らせ一覧未読レイアウト表示
    getRowClass(item: NoticeInfo): string[] | undefined {
      if (this.$service.permission.isDelegateUser()) {
        return ["clickable"];
      } else {
        if (item.isRead === false) {
          return ["clickable", "table-unread"];
        } else {
          return ["clickable", "table-read"];
        }
      }
    },
    /**
     * 日時文字列が有効かどうかをチェック
     * null, undefined, 空文字の場合は無効とみなす
     */
    isDateTimeValid(dateTime: string | null | undefined): boolean {
      return dateTime !== null && dateTime !== undefined && dateTime !== "";
    },
  },
});
</script>
<style lang="scss" scoped>
/* tdにpaddingが入ってカレンダーがずれてしまうので、0を適用 */
:deep(td) {
  padding: 0 !important;
}
/* 日付部分のテキストを中央寄せにする */
:deep(.fc-daygrid-day-top) {
  justify-content: center !important;
}
/* 土曜日の背景色と文字色を変更（ヘッダーとセル） */
:deep(.fc-day-sat) {
  background-color: #eaf4ff !important;
}
:deep(.fc-day-sat .fc-daygrid-day-number),
:deep(.fc-col-header-cell.fc-day-sat) {
  color: #0066cc !important;
}
/* 日曜日の背景色と文字色を変更（ヘッダーとセル） */
:deep(.fc-day-sun) {
  background-color: #ffeaea !important;
}
:deep(.fc-day-sun .fc-daygrid-day-number),
:deep(.fc-col-header-cell.fc-day-sun) {
  color: #d93434 !important;
}
/* 当日（今日）は土日の背景色よりも優先させる */
:deep(.fc-day-today.fc-day-sat),
:deep(.fc-day-today.fc-day-sun) {
  background-color: #fef3c7 !important;
}
/* prev,nextボタン内の要素を中央寄せ */
:deep(.fc-icon) {
  display: flex !important;
  justify-content: center !important;
}
// イベント部分をクリック可能にする
:deep(.fc-event) {
  cursor: pointer !important;
  position: relative; // バッジ配置のため
}
// 右側のボタンを非表示（領域確保のため）
:deep(.fc-toolbar-chunk:last-child .fc-button) {
  visibility: hidden !important;
}

.detail-panel {
  border: none;
  padding: 10px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column; /* 縦向き */
  overflow-y: auto; /* 縦スクロール追加 */

  // 件名の改行
  .title-text {
    white-space: pre-wrap;
    line-height: 1.4;
    margin-right: 8px;
  }
  // メンテナンス内容のスタイル調整
  .maintenance-description {
    // height: 100%;
    flex: 1;
    white-space: pre-wrap;
    line-height: 1.2;
    flex-grow: 1;
  }
  // メッセージテキストの改行
  .message-text {
    white-space: pre-wrap;
    line-height: 1.4;
  }
}
</style>
